import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useHelp } from '../context/HelpProvider';
import { anchorLabel, getHelpContextForRoute } from './helpAnchors';
import HelpContent, { formatDuration } from './HelpContent';

const currentLang = (i18n) => {
    const l = String(i18n?.language || 'en').toLowerCase();
    return l.startsWith('ja') || l.startsWith('jp') ? 'jp' : 'en';
};

// Map each help anchor to the sidebar label key so the modal's feature name is
// localized and stays consistent with the page heading / sidebar menu item.
const ANCHOR_LABEL_KEYS = {
    'documents.overview': 'sideBar.documents',
    'members.overview': 'sideBar.memberList',
    'settings.overview': 'sideBar.settings',
    'conversations.history': 'sideBar.conversationHistory',
    'analytics.overview': 'sideBar.nanalytics',
    'chatbots.settings': 'sideBar.chatbotMenu',
    'agents.overview': 'sideBar.agents',
    'agent.requests': 'sideBar.agentRequest',
    'tickets.overview': 'sideBar.tickets',
};

/**
 * Contextual help trigger. Renders a small "?" icon that is route-aware: it
 * derives its anchor from the current path via getHelpContextForRoute, so
 * pages don't pass any props. On routes with no mapping it renders nothing.
 * (An explicit `anchor`/`label` prop can still override the route detection.)
 * When clicked it opens a modal with the guides for that page; if the page has
 * no published videos the modal shows a friendly empty state.
 */
export default function HelpButton({ anchor: anchorProp, label: labelProp, size = 'small', sx }) {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { getForAnchor } = useHelp();
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const routeCtx = getHelpContextForRoute(pathname);
    const anchor = anchorProp || routeCtx?.anchor || '';
    const rawLabel = labelProp || routeCtx?.label || anchorLabel(anchor);
    const title = labelProp
        ? labelProp
        : (ANCHOR_LABEL_KEYS[anchor] ? t(ANCHOR_LABEL_KEYS[anchor], rawLabel) : rawLabel);

    // Prefer guides in the user's current UI language; fall back to all.
    const items = useMemo(() => {
        const all = getForAnchor(anchor);
        const lang = currentLang(i18n);
        const preferred = all.filter((a) => String(a.language || '') === lang);
        return preferred.length ? preferred : all;
    }, [getForAnchor, anchor, i18n]);

    const selected = useMemo(
        () => items.find((a) => a.articleId === selectedId) || items[0] || null,
        [items, selectedId]
    );

    useEffect(() => {
        if (open && items.length) setSelectedId(items[0].articleId);
    }, [open, items]);

    if (!anchor) return null;

    const hasItems = items.length > 0;

    return (
        <>
            <Tooltip title={t('help.tooltip', 'Help')} placement="top" arrow>
                <IconButton
                    aria-label={t('help.ariaLabel', 'Help: {{feature}}', { feature: title })}
                    onClick={() => setOpen(true)}
                    size={size}
                    sx={{
                    color: '#F26522',
                    p: 0.5,
                    '&:hover': { color: '#d4551a', bgcolor: 'action.hover' },
                        ...sx,
                    }}
                >
                    <HelpOutlineIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '14px' } }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mr: 'auto' }}>
                        {t('help.howToUse', 'How to use this page')}
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 1 }}>
                            · {title}
                        </Box>
                    </Typography>
                    <IconButton aria-label={t('help.close', 'Close')} onClick={() => setOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Body: content left, chapter list right */}
                {!hasItems ? (
                    <Box sx={{ px: 3, py: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <OndemandVideoIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {t('help.noContentTitle', 'No tutorials for this page yet')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', maxWidth: 380 }}>
                            {t('help.noContentBody', 'Video guides for this page will appear here once they are published.')}
                        </Typography>
                    </Box>
                ) : (
                <Box sx={{ display: 'flex', gap: 2.5, p: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {selected ? <HelpContent article={selected} /> : null}
                    </Box>

                    {items.length > 1 ? (
                        <Box sx={{ width: { xs: '100%', sm: 240 }, flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                                {t('help.chapters', 'Chapters')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {items.map((a, idx) => {
                                    const active = a.articleId === selected?.articleId;
                                    return (
                                        <Box
                                            key={a.articleId}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setSelectedId(a.articleId)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(a.articleId); } }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 1.25,
                                                py: 1,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                bgcolor: active ? 'rgba(40,123,214,0.1)' : 'transparent',
                                                border: '1px solid',
                                                borderColor: active ? 'rgba(40,123,214,0.35)' : 'transparent',
                                                transition: 'background-color 0.12s ease',
                                                '&:hover': { bgcolor: active ? 'rgba(40,123,214,0.14)' : 'action.hover' },
                                                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                                            }}
                                        >
                                            <Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                                                <OndemandVideoIcon sx={{ fontSize: 16 }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: active ? 700 : 500, color: active ? 'text.primary' : 'text.secondary' }}>
                                                    {idx + 1}. {a.title}
                                                </Typography>
                                                {formatDuration(a.duration) ? (
                                                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                                                        {formatDuration(a.duration)}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
                )}

                {/* Footer */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {hasItems ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 'auto', color: 'text.disabled' }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: '0.78rem' }}>
                                {t('help.loomNote', 'This is an embedded Loom tutorial.')}
                            </Typography>
                        </Box>
                    ) : <Box sx={{ mr: 'auto' }} />}
                    <Button
                        variant="outlined"
                        startIcon={<MenuBookOutlinedIcon />}
                        onClick={() => { setOpen(false); navigate('/help'); }}
                        sx={{ borderRadius: '8px' }}
                    >
                        {t('help.openFullGuide', 'Open full guide')}
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}
